import React, { useEffect, useState } from "react";
import { trackFunnelEvent } from "../analytics";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  LinearProgress,
  Checkbox,
} from "@mui/material";
import {
  Star,
  TrendingUp,
  EmojiEvents,
  ArrowBack,
  CheckCircle,
  LockOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";

// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  FreeHelpQualificationResult,
  getFreeHelpQualificationResult,
  hasMinimumIndustryExperience,
  IndustryExperienceLevel,
  STUDENT_SITUATIONS,
} from "../utils/freeHelpQualification";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface CoachingPageProps {
  hideNavigation?: boolean;
  trackWelcomeCallEvents?: boolean;
  requireFreeHelpQualification?: boolean;
}

const CoachingPage: React.FC<CoachingPageProps> = ({
  hideNavigation = false,
  trackWelcomeCallEvents = false,
  requireFreeHelpQualification = false,
}) => {
  const APPLICATION_URL =
    "https://cal.com/raymond-jones/strategy-call";
  const loomEmbedOptions =
    "hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true&hide_progress=true&hide_playback_speed=true&hide_chapters=true&hide_cta=true";
  const getLoomId = (loomUrl: string) =>
    loomUrl.match(/loom\.com\/(?:embed|share)\/([^?]+)/)?.[1] || loomUrl;
  const getLoomEmbedUrl = (loomUrl: string) =>
    `https://www.loom.com/embed/${getLoomId(loomUrl)}?${loomEmbedOptions}`;
  const getLoomShareUrl = (loomUrl: string) =>
    `https://www.loom.com/share/${getLoomId(loomUrl)}`;
  const vslLoomUrl =
    "https://www.loom.com/share/40a56a357e7349e7861aab9546fc6974";
  const authorizedWithoutSponsorship =
    "Yes, I'm authorized to work in the U.S. or Canada and won't need sponsorship";
  const [industryExperienceLevel, setIndustryExperienceLevel] =
    useState<IndustryExperienceLevel>("");
  const [isUsCanadaEngineer, setIsUsCanadaEngineer] = useState<
    "yes" | "no" | ""
  >("");
  const [workAuthorizationStatus, setWorkAuthorizationStatus] = useState("");
  const [hasSolved100DsaProblems, setHasSolved100DsaProblems] = useState<
    "yes" | "no" | ""
  >("");
  const [currentSituation, setCurrentSituation] = useState("");
  const [desiredSituation, setDesiredSituation] = useState("");
  const [investmentReadiness, setInvestmentReadiness] = useState("");
  const [applicantName, setApplicantName] = useState("");
  const [applicantEmail, setApplicantEmail] = useState("");
  const [applicantPhone, setApplicantPhone] = useState("");
  const [linkedinProfile, setLinkedinProfile] = useState("");
  const [hasAcceptedPrivacyTerms, setHasAcceptedPrivacyTerms] = useState(false);
  const [privacyConsentTimestamp, setPrivacyConsentTimestamp] = useState("");
  const [freeHelpOutcome, setFreeHelpOutcome] = useState<
    "qualified" | "student_qualified" | "submitted" | null
  >(null);
  const [hasSubmittedFreeHelpApplication, setHasSubmittedFreeHelpApplication] =
    useState(false);
  const [isSubmittingFreeHelpApplication, setIsSubmittingFreeHelpApplication] =
    useState(false);
  const [applicationSubmitError, setApplicationSubmitError] = useState("");
  const [hasVslLoaded, setHasVslLoaded] = useState(false);
  const [vslLoadTimedOut, setVslLoadTimedOut] = useState(false);
  const hasIndustryExperience = industryExperienceLevel
    ? hasMinimumIndustryExperience(industryExperienceLevel)
      ? "yes"
      : "no"
    : "";
  const hasCompletedFreeHelpQualification = freeHelpOutcome !== null;
  const hasRequiredContactDetails = Boolean(
    applicantName.trim() &&
      applicantEmail.trim() &&
      linkedinProfile.trim()
  );
  const hasRequiredFullQualificationDetails = Boolean(
      workAuthorizationStatus &&
      hasSolved100DsaProblems &&
      currentSituation &&
      desiredSituation &&
      investmentReadiness
  );
  const canSubmitFreeHelpApplication =
    hasAcceptedPrivacyTerms &&
    hasRequiredContactDetails &&
    Boolean(industryExperienceLevel) &&
    Boolean(isUsCanadaEngineer) &&
    hasRequiredFullQualificationDetails;
  const applicationAnsweredFields = [
    applicantName,
    applicantEmail,
    linkedinProfile,
    industryExperienceLevel,
    isUsCanadaEngineer,
    workAuthorizationStatus,
    hasSolved100DsaProblems,
    currentSituation,
    hasAcceptedPrivacyTerms ? "privacy-consent" : "",
    desiredSituation,
    investmentReadiness,
  ].filter(Boolean).length;
  const applicationVisibleFieldCount = 11;
  const applicationProgress = canSubmitFreeHelpApplication
    ? 100
    : Math.min(
        95,
        Math.round(
          (applicationAnsweredFields / applicationVisibleFieldCount) * 100
        )
      );
  const isBookableFreeHelpOutcome =
    freeHelpOutcome === "qualified" || freeHelpOutcome === "student_qualified";
  const freeHelpDestinationUrl =
    isBookableFreeHelpOutcome
      ? APPLICATION_URL
      : "#free-help-qualification";
  const hasExternalFreeHelpDestination =
    !requireFreeHelpQualification ||
    isBookableFreeHelpOutcome;
  const ctaUrl = requireFreeHelpQualification
    ? hasCompletedFreeHelpQualification
      ? freeHelpDestinationUrl
      : "#free-help-qualification"
    : APPLICATION_URL;
  const ctaTarget =
    requireFreeHelpQualification && !hasExternalFreeHelpDestination
      ? undefined
      : "_blank";
  const ctaRel =
    requireFreeHelpQualification && !hasExternalFreeHelpDestination
      ? undefined
      : "noopener noreferrer";
  const ctaLabel = requireFreeHelpQualification
    ? isBookableFreeHelpOutcome
      ? "Book Your Free Advice Call"
      : freeHelpOutcome === "submitted"
      ? "Application Submitted"
      : "Start Your Application"
    : "Apply Now";

  useEffect(() => {
    document.title = 'Tech Accelerator Coaching | Ray Jones';
  }, []);

  useEffect(() => {
    if (hasVslLoaded) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setVslLoadTimedOut(true);
    }, 8000);

    return () => window.clearTimeout(timeoutId);
  }, [hasVslLoaded]);

  useEffect(() => {
    if (!trackWelcomeCallEvents || typeof window.fbq !== "function") {
      return;
    }

    window.fbq("trackCustom", "WelcomeCallWithRayView", {
      content_name: "Welcome Call With Ray",
      content_category: "coaching_application",
      content_type: "landing_page",
    });
  }, [trackWelcomeCallEvents]);

  useEffect(() => {
    if (!requireFreeHelpQualification || typeof window.fbq !== "function") {
      return;
    }

    window.fbq("trackCustom", "FreeHelpWithRayView", {
      content_name: "Free Help With Ray",
      content_category: "coaching_application",
      content_type: "qualification_landing_page",
    });
  }, [requireFreeHelpQualification]);

  const trackMetaEvent = (
    eventName: string,
    params: {
      content_name: string;
      content_category: string;
      content_type: string;
      industry_experience_level?: string;
      current_situation?: string;
      desired_situation?: string;
      investment_readiness?: string;
      is_us_canada_engineer?: string;
      work_authorization_status?: string;
      has_solved_100_dsa_problems?: string;
    },
    eventId?: string
  ) => {
    if (typeof window.fbq !== "function") {
      return;
    }

    window.fbq(
      "trackCustom",
      eventName,
      eventId ? { ...params, event_id: eventId } : params,
      eventId ? { eventID: eventId } : undefined
    );
  };

  const sendServerMetaEvent = async (
    eventName: string,
    eventId: string,
    contentCategory:
      | "qualified"
      | "student_qualified"
      | "needs_skool"
      | "work_authorization"
  ) => {
    try {
      await fetch("/api/meta-event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventName,
          eventId,
          eventSourceUrl: window.location.href,
          userData: {
            name: applicantName,
            email: applicantEmail,
            phone: applicantPhone,
          },
          customData: {
            content_name: "Free Help With Ray",
            content_category: contentCategory,
            content_type: "qualification_result",
            industry_experience_level: industryExperienceLevel,
            current_situation: currentSituation,
            desired_situation: desiredSituation,
            investment_readiness: investmentReadiness,
            is_us_canada_engineer: isUsCanadaEngineer,
            work_authorization_status: workAuthorizationStatus,
            has_solved_100_dsa_problems: hasSolved100DsaProblems,
          },
        }),
      });
    } catch (error) {
      console.error("Failed to send server Meta event:", error);
    }
  };

  const saveApplication = async (
    qualificationResult:
      | "qualified"
      | "student_qualified"
      | "needs_skool"
      | "work_authorization"
  ) => {
    const response = await fetch("/api/application-submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        qualificationResult,
        application: {
          name: applicantName,
          email: applicantEmail,
          phone: applicantPhone,
          linkedinProfile,
          industryExperienceLevel,
          hasIndustryExperience,
          isUsCanadaEngineer,
          workAuthorizationStatus,
          hasSolved100DsaProblems,
          currentSituation,
          desiredSituation,
          investmentReadiness,
          privacyConsent: hasAcceptedPrivacyTerms,
          privacyConsentTimestamp,
        },
      }),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => null);
      throw new Error(result?.error || "The application could not be saved.");
    }

    return response.json();
  };

  const handleApplyClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (requireFreeHelpQualification && !hasCompletedFreeHelpQualification) {
      event.preventDefault();
      document
        .getElementById("free-help-qualification")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (trackWelcomeCallEvents) {
      trackMetaEvent("WelcomeCallWithRayApplyClick", {
        content_name: "Welcome Call With Ray",
        content_category: "coaching_application",
        content_type: "calendly_click",
      });
    }

    if (requireFreeHelpQualification) {
      const contentCategory =
        freeHelpOutcome === "student_qualified"
          ? "student_qualified"
          : freeHelpOutcome === "qualified"
          ? "qualified"
          : "needs_skool";

      trackMetaEvent("FreeHelpWithRayCtaClick", {
        content_name: "Free Help With Ray",
        content_category: contentCategory,
        content_type:
          isBookableFreeHelpOutcome
            ? "calendly_click"
            : "application_status",
      });
    }
  };

  const handleFreeHelpQualificationSubmit = async () => {
    if (!canSubmitFreeHelpApplication || isSubmittingFreeHelpApplication) {
      return;
    }

    const contentCategory: FreeHelpQualificationResult =
      getFreeHelpQualificationResult({
        industryExperienceLevel,
        isUsCanadaEngineer,
        workAuthorizationStatus,
        hasSolved100DsaProblems,
        currentSituation,
        investmentReadiness,
      });
    const isBookable =
      contentCategory === "qualified" || contentCategory === "student_qualified";
    const eventName = contentCategory === "qualified"
      ? "FreeHelpWithRayQualified"
      : contentCategory === "student_qualified"
      ? "FreeHelpWithRayStudentQualified"
      : contentCategory === "work_authorization"
      ? "FreeHelpWithRayWorkAuthorizationDisqualified"
      : "FreeHelpWithRayNeedsSkool";
    const eventId = `free-help-${contentCategory}-${Date.now()}`;
    setApplicationSubmitError("");
    setIsSubmittingFreeHelpApplication(true);

    try {
      await saveApplication(contentCategory);
      trackFunnelEvent("application_submitted", { form_id: "free_help", page_path: window.location.pathname });
      setHasSubmittedFreeHelpApplication(true);
      window.sessionStorage.setItem(
        "free-help-application-submitted",
        "true"
      );

      trackMetaEvent(
        eventName,
        {
          content_name: "Free Help With Ray",
          content_category: contentCategory,
          content_type: "qualification_result",
          industry_experience_level: industryExperienceLevel,
          current_situation: currentSituation,
          desired_situation: desiredSituation,
          investment_readiness: investmentReadiness,
          is_us_canada_engineer: isUsCanadaEngineer,
          work_authorization_status: workAuthorizationStatus,
          has_solved_100_dsa_problems: hasSolved100DsaProblems,
        },
        eventId
      );
      await sendServerMetaEvent(eventName, eventId, contentCategory);

      if (isBookable) {
        setFreeHelpOutcome(contentCategory);
        trackFunnelEvent("booking_click", { destination: "cal.com", page_path: window.location.pathname });
        window.location.assign(APPLICATION_URL);
        return;
      }

      setFreeHelpOutcome("submitted");
    } catch (error) {
      setHasSubmittedFreeHelpApplication(false);
      setApplicationSubmitError(
        error instanceof Error
          ? error.message
          : "The application could not be saved. Please try again."
      );
    } finally {
      setIsSubmittingFreeHelpApplication(false);
    }
  };

  const handleWorkAuthorizationStatusChange = (value: string) => {
    setWorkAuthorizationStatus(value);
    setFreeHelpOutcome(null);
  };

  const handleHasSolved100DsaProblemsChange = (value: "yes" | "no") => {
    setHasSolved100DsaProblems(value);
    setFreeHelpOutcome(null);
  };

  const choiceFieldSx = {
    borderColor: "#434C5E",
    color: "text.secondary",
    justifyContent: "flex-start",
    textAlign: "left",
    textTransform: "none",
    fontWeight: 700,
    lineHeight: 1.35,
    minHeight: 52,
    minWidth: { xs: "100%", sm: 210 },
    flex: "1 1 210px",
    px: 2,
    py: 1.35,
    borderRadius: 2.5,
    "&:hover": {
      borderColor: "#88C0D0",
      bgcolor: "rgba(136, 192, 208, 0.08)",
    },
    "&.MuiButton-contained": {
      color: "black",
      boxShadow: "0 10px 24px -16px rgba(136, 192, 208, 0.8)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "rgba(46, 52, 64, 0.72)",
      borderRadius: 2,
      "& fieldset": {
        borderColor: "#434C5E",
      },
      "&:hover fieldset": {
        borderColor: "#88C0D0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#88C0D0",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
    },
    "& .MuiInputBase-input": {
      color: "text.primary",
    },
  };

  const renderChoiceField = (
    label: string,
    value: string,
    setValue: (value: string) => void,
    options: string[]
  ) => (
    <Box sx={{ scrollMarginTop: 96 }}>
      <Typography
        variant="subtitle1"
        sx={{ color: "text.primary", fontWeight: 800, mb: 1.5 }}
      >
        {label}
        <Box component="span" sx={{ color: "#88C0D0", ml: 0.5 }}>
          *
        </Box>
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {options.map((option) => (
          <Button
            key={option}
            variant={value === option ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setValue(option)}
            sx={choiceFieldSx}
            startIcon={
              value === option ? (
                <CheckCircle sx={{ fontSize: 18 }} />
              ) : undefined
            }
          >
            {option}
          </Button>
        ))}
      </Box>
    </Box>
  );

  // Graduate outcomes data
  const outcomes = [
    { name: "RJ", gotOffer: true, under90Days: true, salary: 180000, time: 38 },
    { name: "Matt", gotOffer: true, under90Days: true, salary: 160000, time: 45 },
    { name: "Andrew", gotOffer: true, under90Days: true, salary: 255000, time: 22 },
    { name: "Chen", gotOffer: true, under90Days: true, salary: 130000, time: 68 },
    { name: "Cory", gotOffer: true, under90Days: true, salary: null, time: null },
    { name: "Mosef", gotOffer: true, under90Days: false, salary: null, time: null },
  ];

  const under90Outcomes = outcomes.filter((outcome) => outcome.under90Days);

  // Chart data (only those with reported salaries)
  const chartData = {
    labels: outcomes.filter((o) => o.salary).map((o) => o.name),
    datasets: [
      {
        label: "Reported Offer Salary ($)",
        data: outcomes
          .filter((o) => o.salary)
          .map((o) => o.salary as number),
        backgroundColor: ["#88C0D0", "#81A1C1", "#5E81AC", "#A3BE8C"],
        borderColor: ["#88C0D0", "#81A1C1", "#5E81AC", "#A3BE8C"],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Reported Offer Salaries",
        font: { size: 18 },
        color: "#ECEFF4",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => "$" + Number(value).toLocaleString(),
          color: "#ECEFF4",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
      x: {
        ticks: {
          color: "#ECEFF4",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  };

  // Summary stats
  const placed = outcomes.filter((o) => o.gotOffer).length;
  const total = outcomes.length;
  const offerRate = Math.round((placed / total) * 100);
  const under90Rate = Math.round((under90Outcomes.length / total) * 100);
  const getMedian = (values: number[]) => {
    if (!values.length) {
      return null;
    }

    const sortedValues = [...values].sort((a, b) => a - b);
    const middleIndex = Math.floor(sortedValues.length / 2);

    if (sortedValues.length % 2 === 1) {
      return sortedValues[middleIndex];
    }

    return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
  };

  const reportedSalaries = outcomes
    .filter((o) => o.salary)
    .map((o) => o.salary as number);
  const medianSalary = getMedian(reportedSalaries);

const testimonials = [
  {
    name: "Andrew",
    company: "C3.ai",
    salary: "$255k",
    quote:
      "I got an offer at C3AI, Raymond helped me a ton with interviewing and preparing for interviews. This would have taken a lot longer if I didn't have his help. He's a top 3% LeetCoder so it really helps to have someone on that level give you tips and feedback.",
    avatar: "A",
    loomUrl:
      "https://www.loom.com/embed/32f365aebbc4426b8cd098535f54f1ac?sid=cef9de69-edee-4889-a344-6f88a5e44e7e&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
  },
  {
    name: "Om",
    company: "Google",
    salary: "$180k",
    quote:
      "After working with Raymond, I passed Google's onsite interview with confidence. His mock interviews prepared me for every scenario.",
    avatar: "O",
    loomUrl:
      "https://www.loom.com/embed/c8ba8a64b5864dc79d1e6520af3f1ee3?sid=aec93f14-b44a-473c-91f3-ad701cff191b&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
  },
  {
    name: "RJ",
    company: "Fortune 500",
    salary: "$179k",
    quote:
      "He's been instrumental in my process getting a ton of interviews, getting a ton of on-sites, and ultimately landing a job offer where I'm making considerably more than my current role. His tactics are really clever and have increased my odds significantly.",
    avatar: "R",
    loomUrl:
      "https://www.loom.com/embed/83e83110c4634fe0b62dd351d8b758f6?sid=a668ef97-5408-46c5-9679-b3fda3126569&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
    hidden: true,
  },
  {
    name: "Matt",
    company: "Affirm",
    salary: "$160k",
    quote:
      "The time I spent with Raymond was crucial for my interviews, especially the behavioral stuff. He kept me accountable and really sped up the process. Without this, I wouldn't have been as confident. Everything really added up to help me land my offer.",
    avatar: "M",
    loomUrl:
      "https://www.loom.com/embed/0ef7eea40968445ea612570737a3de11?sid=1f40c316-6be3-4386-91df-78d25a714539&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true&hide_progress=true&hide_playback_speed=true&hide_chapters=true&hide_cta=true",
  },
  {
    name: "Chris",
    company: "",
    salary: "",
    quote:
      "This kind of practice one-on-one really helps you cement down the patterns you need to actually do well in the interview. And like, I definitely recommend it to everybody really.",
    avatar: "C",
    loomUrl:
      "https://www.loom.com/embed/b8126cb7c64b48799b6b81d3d5c967b3?sid=9d4749f2-c59c-42ec-bf19-0abe5c01c3e3&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true&hide_progress=true&hide_playback_speed=true&hide_chapters=true&hide_cta=true",
  },
  {
    name: "Hassan",
    company: "",
    salary: "",
    quote:
      "Mock interviews have been really helpful. I feel that Ray has been simulating the real-time interviews that happen at these big tech companies.",
    avatar: "H",
    loomUrl:
      "https://www.loom.com/embed/d6fcb15c2f32452da7cd4ff9201df1ff?sid=dc6a6798-0daf-4319-b3df-0d6f506230a9&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true&hide_progress=true&hide_playback_speed=true&hide_chapters=true&hide_cta=true",
  },
];
  const visibleTestimonials = testimonials.filter(
    (testimonial) => !testimonial.hidden
  );
  const faqs = [
    {
      question: "Who is this free advice call for?",
      answer:
        "This is for working software engineers who already have a job, but feel underpaid, stuck, burned out, or ready to move into a stronger engineering role. If you want a clear plan for interviewing again without studying full-time, this call is for you.",
    },
    {
      question: "What will we cover on the call?",
      answer:
        "We will look at where you are now, what kind of role or salary jump you want, what is blocking you, and what your highest-leverage next steps should be. The goal is to leave with a clearer path, not vague motivation.",
    },
    {
      question: "Is this only for FAANG or Big Tech?",
      answer:
        "No. Big Tech prep is useful because it raises your interview bar, but the same strategy can help with strong startups, public companies, remote roles, and higher-paying senior engineering opportunities.",
    },
    {
      question: "I only have a few hours a week. Can this still work?",
      answer:
        "Yes. The whole point is to build a realistic plan around your current job. We will focus on the highest-impact work first so you are not randomly grinding LeetCode after work with no system.",
    },
    {
      question: "Do I need to be actively interviewing already?",
      answer:
        "No. The best time to plan is usually before you are desperate to leave. If you are unhappy with your current role or know you want a compensation jump, we can map out what to do before applications and interviews start.",
    },
    {
      question: "Is this a sales call?",
      answer:
        "It is a free advice and fit call. If I think I can help you further, I may explain what working together looks like. If not, I will still point you toward the most useful next step I see.",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      {!hideNavigation && <Navigation />}

      {/* Hero Section */}
      <Box sx={{ pt: hideNavigation ? 6 : 12, pb: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            {requireFreeHelpQualification && (
              <>
                <Typography
                  variant="overline"
                  sx={{
                    color: "#88C0D0",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    mb: 2,
                    display: "block",
                  }}
                >
                  Invite Only
                </Typography>

              </>
            )}

            <Typography
              variant="h2"
              gutterBottom
              sx={{ color: "text.primary", mb: 3, fontWeight: "bold" }}
            >
              Land Your Dream{" "}
              <span style={{ color: "#88C0D0" }}>Job</span>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "text.secondary",
                mb: 6,
                maxWidth: 800,
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              I'm top 2.35% in competitive programming worldwide. I've worked at a
              Fortune 50 company and have 5+ years of experience. I want you to
              get your job as fast as possible. Every month you don't have a
              job is money lost.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                mb: 6,
              }}
            >
              <Chip
                icon={<Star />}
                label="Top 2.35% Competitive Programming"
                sx={{
                  bgcolor: "rgba(136, 192, 208, 0.1)",
                  color: "secondary.main",
                  border: "1px solid #88C0D0",
                  fontSize: "1rem",
                  py: 3,
                  px: 1,
                }}
              />
              <Chip
                icon={<TrendingUp />}
                label="Fortune 50 Experience"
                sx={{
                  bgcolor: "rgba(136, 192, 208, 0.1)",
                  color: "secondary.main",
                  border: "1px solid #88C0D0",
                  fontSize: "1rem",
                  py: 3,
                  px: 1,
                }}
              />
              <Chip
                icon={<EmojiEvents />}
                label="5+ Years Experience"
                sx={{
                  bgcolor: "rgba(136, 192, 208, 0.1)",
                  color: "secondary.main",
                  border: "1px solid #88C0D0",
                  fontSize: "1rem",
                  py: 3,
                  px: 1,
                }}
              />
            </Box>
          </Box>

          {/* VSL Video Section */}
          <Box sx={{ maxWidth: 900, mx: "auto", mb: 8 }}>
            <Card sx={{ bgcolor: "background.paper", border: "2px solid #88C0D0" }}>
              <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
                <iframe
                  src={getLoomEmbedUrl(vslLoomUrl)}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  onLoad={() => {
                    setHasVslLoaded(true);
                    setVslLoadTimedOut(false);
                  }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  title="VSL Video"
                />
                {vslLoadTimedOut && !hasVslLoaded && (
                  <Box
                    role="status"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 1,
                      bgcolor: "rgba(15, 18, 24, 0.96)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      p: 3,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "text.primary" }}>
                      The video is taking longer than expected to load.
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      A privacy extension or network filter may be blocking the
                      embedded player. Open the video directly to keep watching.
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      href={getLoomShareUrl(vslLoomUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "black" }}
                    >
                      Open video on Loom
                    </Button>
                  </Box>
                )}
              </Box>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Typography variant="h5" sx={{ color: "text.primary", mb: 2 }}>
                  Watch: How I Help Engineers Land $200k+ Jobs
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  My exact system for acing FAANG interviews and maximizing
                  compensation
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  href={getLoomShareUrl(vslLoomUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 2 }}
                >
                  Watch on Loom
                </Button>
              </CardContent>
            </Card>

            {requireFreeHelpQualification && (
              <Paper
                id="free-help-qualification"
                sx={{
                  mt: 5,
                  p: { xs: 2, sm: 3, md: 4 },
                  bgcolor: "rgba(15, 18, 24, 0.82)",
                  border: "1px solid #434C5E",
                  borderRadius: 4,
                  boxShadow: "0 28px 80px -42px rgba(136, 192, 208, 0.45)",
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Chip
                    label="Takes about 2 minutes"
                    size="small"
                    sx={{
                      mb: 2,
                      bgcolor: "rgba(136, 192, 208, 0.12)",
                      color: "secondary.main",
                      fontWeight: 800,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      color: "text.primary",
                      fontWeight: 900,
                      letterSpacing: "-0.035em",
                      mb: 1.5,
                    }}
                  >
                    Apply for free coaching to land a $200k/year offer
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      maxWidth: 680,
                      lineHeight: 1.7,
                      mb: 2.5,
                    }}
                  >
                    Answer a few focused questions so I can recommend the most
                    useful next step. I personally review every application.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      Application progress
                    </Typography>
                    <Typography variant="caption" sx={{ color: "secondary.main", fontWeight: 900 }}>
                      {applicationProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={applicationProgress}
                    color="secondary"
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      bgcolor: "rgba(67, 76, 94, 0.65)",
                      "& .MuiLinearProgress-bar": { borderRadius: 999 },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2.25, md: 3 },
                      bgcolor: "rgba(46, 52, 64, 0.42)",
                      borderColor: "#434C5E",
                      borderRadius: 3,
                    }}
                  >
                    <Typography variant="overline" sx={{ color: "secondary.main", fontWeight: 900 }}>
                      Step 1 of 3
                    </Typography>
                    <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 900, mb: 0.5 }}>
                      Your details
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                      Where I can reach you if there’s a strong fit.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                    <TextField
                      required
                      label="Full name"
                      autoComplete="name"
                      value={applicantName}
                      onChange={(event) => setApplicantName(event.target.value)}
                      fullWidth
                      sx={textFieldSx}
                    />
                    <TextField
                      required
                      label="Email address"
                      type="email"
                      autoComplete="email"
                      value={applicantEmail}
                      onChange={(event) => setApplicantEmail(event.target.value)}
                      fullWidth
                      sx={textFieldSx}
                    />
                    <TextField
                      label="Phone number (optional)"
                      type="tel"
                      autoComplete="tel"
                      placeholder="+1 555 123 4567"
                      helperText="Optional — include your country code"
                      value={applicantPhone}
                      onChange={(event) => setApplicantPhone(event.target.value)}
                      fullWidth
                      sx={textFieldSx}
                    />
                    <TextField
                      required
                      label="LinkedIn profile"
                      type="url"
                      autoComplete="url"
                      placeholder="https://linkedin.com/in/your-profile"
                      helperText="Paste the full profile URL"
                      value={linkedinProfile}
                      onChange={(event) => setLinkedinProfile(event.target.value)}
                      fullWidth
                      sx={textFieldSx}
                    />
                    </Box>
                    <Box
                      sx={{
                        mt: 2.5,
                        p: 2,
                        borderRadius: 2.5,
                        bgcolor: "rgba(136, 192, 208, 0.07)",
                        border: "1px solid rgba(136, 192, 208, 0.24)",
                      }}
                    >
                      <Box
                        component="label"
                        htmlFor="application-consent"
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.25,
                          cursor: "pointer",
                        }}
                      >
                        <Checkbox
                          id="application-consent"
                          checked={hasAcceptedPrivacyTerms}
                          onChange={(event) => {
                            const isAccepted = event.target.checked;
                            setHasAcceptedPrivacyTerms(isAccepted);
                            setPrivacyConsentTimestamp(
                              isAccepted ? new Date().toISOString() : ""
                            );
                          }}
                          color="secondary"
                          sx={{ p: 0.25, mt: 0.1 }}
                          inputProps={{
                            required: true,
                            "aria-label":
                              "Agree to the Terms and Privacy Policy",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(236,239,244,0.82)",
                            lineHeight: 1.6,
                          }}
                        >
                          By entering your information, you consent to your data
                          being saved in accordance with our{" "}
                          <Link
                            to="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#88C0D0", fontWeight: 800 }}
                          >
                            Terms
                          </Link>{" "}
                          &amp;{" "}
                          <Link
                            to="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#88C0D0", fontWeight: 800 }}
                          >
                            Privacy Policy
                          </Link>
                          .
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2.25, md: 3 },
                      bgcolor: "rgba(46, 52, 64, 0.42)",
                      borderColor: "#434C5E",
                      borderRadius: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography variant="overline" sx={{ color: "secondary.main", fontWeight: 900 }}>
                        Step 2 of 3
                      </Typography>
                      <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 900, mb: 0.5 }}>
                        Current Situation
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Tell me where you are starting from today.
                      </Typography>
                    </Box>

                  {renderChoiceField(
                    "How many years of paid, professional software engineering experience do you have?",
                    industryExperienceLevel,
                    (value) => {
                      setIndustryExperienceLevel(
                        value as IndustryExperienceLevel
                      );
                      setFreeHelpOutcome(null);
                    },
                    [
                      "0 years",
                      "Less than 1 year",
                      "1-2 years",
                      "3-5 years",
                      "6+ years",
                    ]
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 700, mb: 1.25 }}>
                      Are you currently based in the USA or Canada?<Box component="span" sx={{ color: "#88C0D0", ml: 0.5 }}>*</Box>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant={isUsCanadaEngineer === "yes" ? "contained" : "outlined"}
                        color="secondary"
                        onClick={() => {
                          setIsUsCanadaEngineer("yes");
                          setFreeHelpOutcome(null);
                        }}
                        sx={choiceFieldSx}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={isUsCanadaEngineer === "no" ? "contained" : "outlined"}
                        color="secondary"
                        onClick={() => {
                          setIsUsCanadaEngineer("no");
                          setFreeHelpOutcome(null);
                        }}
                        sx={choiceFieldSx}
                      >
                        No
                      </Button>
                    </Box>
                  </Box>

                  {renderChoiceField(
                    "Are you authorized to work in the U.S. or Canada without employer visa sponsorship now or in the future?",
                    workAuthorizationStatus,
                    handleWorkAuthorizationStatusChange,
                    [
                      authorizedWithoutSponsorship,
                      "No, I'm not authorized to work",
                      "Yes, I require sponsorship now",
                      "Yes, I'll require sponsorship in the future",
                    ]
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 700, mb: 1.25 }}>
                      Have you solved at least 100 DSA or LeetCode-style problems?<Box component="span" sx={{ color: "#88C0D0", ml: 0.5 }}>*</Box>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant={hasSolved100DsaProblems === "yes" ? "contained" : "outlined"}
                        color="secondary"
                        onClick={() => handleHasSolved100DsaProblemsChange("yes")}
                        sx={choiceFieldSx}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={hasSolved100DsaProblems === "no" ? "contained" : "outlined"}
                        color="secondary"
                        onClick={() => handleHasSolved100DsaProblemsChange("no")}
                        sx={choiceFieldSx}
                      >
                        No, not yet
                      </Button>
                    </Box>
                  </Box>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2.25, md: 3 },
                      bgcolor: "rgba(46, 52, 64, 0.42)",
                      borderColor: "#434C5E",
                      borderRadius: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography variant="overline" sx={{ color: "secondary.main", fontWeight: 900 }}>
                        Step 3 of 3
                      </Typography>
                      <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 900, mb: 0.5 }}>
                        Your current goals
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        This helps me understand how I may be able to help.
                      </Typography>
                    </Box>
                    {renderChoiceField(
                      "What is your current situation?",
                      currentSituation,
                      (value) => {
                        setCurrentSituation(value);
                        setFreeHelpOutcome(null);
                      },
                      [
                        "Employed SWE but underpaid",
                        "Employed SWE but dislike my job",
                        "Previously employed SWE",
                        "Interviewing right now",
                        ...STUDENT_SITUATIONS,
                      ]
                    )}

                    {renderChoiceField(
                      "What is your desired situation?",
                      desiredSituation,
                      setDesiredSituation,
                      [
                        "Land a higher-paying SWE role",
                        "Land a software engineering internship",
                        "Land my first software engineering role",
                        "Get more interviews",
                        "Move to a stronger company",
                        "Prepare for interviews I already have",
                      ]
                    )}

                    {renderChoiceField(
                      "Would you be ready and willing to invest if we found the right plan?",
                      investmentReadiness,
                      setInvestmentReadiness,
                      [
                        "Yes, if the plan makes sense",
                        "Maybe, I need to understand the fit first",
                        "No, I'm only looking for free help right now",
                      ]
                    )}
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2.25, md: 3 },
                      bgcolor: "rgba(23, 27, 36, 0.72)",
                      borderColor: canSubmitFreeHelpApplication
                        ? "secondary.main"
                        : "#434C5E",
                      borderRadius: 3,
                    }}
                  >
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    disabled={
                      !canSubmitFreeHelpApplication ||
                      isSubmittingFreeHelpApplication ||
                      hasSubmittedFreeHelpApplication
                    }
                    onClick={handleFreeHelpQualificationSubmit}
                    sx={{
                      color: "black",
                      py: 2,
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {isSubmittingFreeHelpApplication
                      ? "Saving application..."
                      : hasSubmittedFreeHelpApplication
                      ? "Application submitted"
                      : "Submit application →"}
                  </Button>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.75,
                      mt: 1.5,
                    }}
                  >
                    <LockOutlined sx={{ color: "text.secondary", fontSize: 16 }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Your application is saved before any booking redirect.
                    </Typography>
                  </Box>

                  {applicationSubmitError && (
                    <Alert severity="error" sx={{ textAlign: "left", mt: 2 }}>
                      {applicationSubmitError} Your answers are still on this
                      page—please try again.
                    </Alert>
                  )}

                  {freeHelpOutcome === "submitted" && (
                    <Alert severity="success" sx={{ textAlign: "left", mt: 2 }}>
                      Your application was submitted successfully. I’ll personally
                      review your answers and follow up if I can help.
                    </Alert>
                  )}
                  </Paper>
                  </Box>
              </Paper>
            )}

            {!requireFreeHelpQualification && (
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  sx={{
                    color: "black",
                    px: 7,
                    py: 2.25,
                    fontWeight: "bold",
                    fontSize: "1.35rem",
                    letterSpacing: "0.04em",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                    transition: "transform 0.2s",
                  }}
                  href={ctaUrl}
                  target={ctaTarget}
                  rel={ctaRel}
                  onClick={handleApplyClick}
                >
                  APPLY NOW
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Graduate Outcomes Section */}
      <Box sx={{ py: 8, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: "text.primary", mb: 2 }}
          >
            📊 Client <span style={{ color: "#88C0D0" }}>Offer Outcomes</span> for 2024 to 2025
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{ color: "text.secondary", mb: 6 }}
          >
            I believe in transparency. Below are reported outcomes from the 6
            paid participants tracked in this cohort.
          </Typography>

          {/* Summary Stats */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              flexWrap: "wrap",
              mb: 6,
              gap: 2,
            }}
          >
            <Paper
              sx={{
                p: 3,
                border: "2px solid #88C0D0",
                bgcolor: "rgba(136, 192, 208, 0.1)",
                minWidth: 200,
                textAlign: "center",
              }}
            >
              <Typography variant="h3" sx={{ color: "#88C0D0", mb: 1 }}>
                {offerRate}%
              </Typography>
              <Typography sx={{ color: "text.primary" }}>
                Received Job Offers
              </Typography>
            </Paper>
            <Paper
              sx={{
                p: 3,
                border: "2px solid #88C0D0",
                bgcolor: "rgba(136, 192, 208, 0.1)",
                minWidth: 200,
                textAlign: "center",
              }}
            >
              <Typography variant="h3" sx={{ color: "#88C0D0", mb: 1 }}>
                {under90Rate}%
              </Typography>
              <Typography sx={{ color: "text.primary" }}>
                Got Offers in Under 90 Days
              </Typography>
            </Paper>
            <Paper
              sx={{
                p: 3,
                border: "2px solid #88C0D0",
                bgcolor: "rgba(136, 192, 208, 0.1)",
                minWidth: 200,
                textAlign: "center",
              }}
            >
              <Typography variant="h3" sx={{ color: "#88C0D0", mb: 1 }}>
                {medianSalary ? `$${medianSalary.toLocaleString()}` : "N/A"}
              </Typography>
              <Typography sx={{ color: "text.primary" }}>
                Median Reported Offer
              </Typography>
            </Paper>
          </Box>

          {/* Outcomes Table */}
          <Typography
            variant="h5"
            align="center"
            sx={{ color: "text.primary", fontWeight: 800, mb: 2 }}
          >
            100% of Tracked Participants Received Job Offers
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 6, bgcolor: "background.default" }}
          >
            <Table>
              <TableHead sx={{ bgcolor: "#88C0D0" }}>
                <TableRow>
                  <TableCell sx={{ color: "#2E3440", fontWeight: "bold" }}>
                    First Name
                  </TableCell>
                  <TableCell sx={{ color: "#2E3440", fontWeight: "bold" }}>
                    Received Job Offer
                  </TableCell>
                  <TableCell sx={{ color: "#2E3440", fontWeight: "bold" }}>
                    Reported Offer Amount
                  </TableCell>
                  <TableCell sx={{ color: "#2E3440", fontWeight: "bold" }}>
                    Offer Timing
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outcomes.map((outcome, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: "text.primary" }}>
                      {outcome.name}
                    </TableCell>
                    <TableCell sx={{ color: "text.primary" }}>
                      ✅ Yes
                    </TableCell>
                    <TableCell sx={{ color: "text.primary" }}>
                      {outcome.salary
                        ? `$${outcome.salary.toLocaleString()}`
                        : "Not Reported"}
                    </TableCell>
                    <TableCell sx={{ color: "text.primary" }}>
                      {outcome.time
                        ? `${outcome.time} days`
                        : outcome.under90Days
                        ? "Under 90 days"
                        : "90+ days"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography
            variant="h5"
            align="center"
            sx={{ color: "text.primary", fontWeight: 800, mb: 2 }}
          >
            {under90Rate}% Got Offers in Under 90 Days
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 6, bgcolor: "background.default" }}
          >
            <Table>
              <TableHead sx={{ bgcolor: "#88C0D0" }}>
                <TableRow>
                  <TableCell sx={{ color: "#2E3440", fontWeight: "bold" }}>
                    First Name
                  </TableCell>
                  <TableCell sx={{ color: "#2E3440", fontWeight: "bold" }}>
                    Under 90 Days?
                  </TableCell>
                  <TableCell sx={{ color: "#2E3440", fontWeight: "bold" }}>
                    Time to Offer
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {under90Outcomes.map((outcome, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: "text.primary" }}>
                      {outcome.name}
                    </TableCell>
                    <TableCell sx={{ color: "text.primary" }}>
                      ✅ Yes
                    </TableCell>
                    <TableCell sx={{ color: "text.primary" }}>
                      {outcome.time ? `${outcome.time} days` : "Under 90 days"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Chart */}
          <Box sx={{ maxWidth: 600, mx: "auto", mb: 4 }}>
            <Paper sx={{ p: 3, bgcolor: "background.default" }}>
              <Bar data={chartData} options={chartOptions} />
            </Paper>
          </Box>

          {/* Disclaimer */}
          <Typography
            variant="body2"
            align="center"
            sx={{
              color: "text.secondary",
              mt: 4,
              fontStyle: "italic",
              lineHeight: 1.6,
            }}
          >
            ⚠️ <strong>Results Disclaimer:</strong> The stated outcomes (including percentage success rates, salary figures, and timelines) are based on data collected from students who paid for and participated in our annual support program between 2024-2025. These results represent self-reported data from program participants and have been verified through offer letters where available.
            <br /><br />
            <strong>Individual Results May Vary:</strong> Past performance does not guarantee future results. Your individual outcomes will depend on factors including but not limited to: your prior experience, technical skills, interview performance, market conditions, geographic location, job search effort, and economic climate.
            <br /><br />
            <strong>No Guarantees:</strong> We do not guarantee employment, interviews, job offers, or specific salary outcomes. Participation in our program does not ensure any particular result.
            <br /><br />
            <strong>Data Methodology:</strong> The offer rate is calculated based on all 6 paying participants tracked in this cohort receiving a job offer. The under-90-day rate is calculated separately as 5 of those 6 tracked participants receiving an offer within 90 days of program start. Salary figures and exact timelines are shown where reported and documented.
            <br /><br />
            <strong>Privacy:</strong> Student names and identifying information may be changed or omitted to protect privacy. Company names and salary figures represent actual reported outcomes.
          </Typography>
        </Container>
      </Box>
      {/* Testimonials Section */}
      <Box sx={{ py: 8, bgcolor: "background.default" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: "text.primary", mb: 6 }}
          >
            Hear From People I've <span style={{ color: "#88C0D0" }}>Helped</span>
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              gap: 4,
            }}
          >
            {visibleTestimonials.map((testimonial, index) => (
              <Box key={index}>
                <Card
                  sx={{
                    bgcolor: "background.paper",
                    height: "100%",
                    border: "2px solid #434C5E",
                    "&:hover": {
                      border: "2px solid #88C0D0",
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {testimonial.loomUrl && (
                    <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
                      <iframe
                        src={getLoomEmbedUrl(testimonial.loomUrl)}
                        frameBorder="0"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                        title={`${testimonial.name} Testimonial`}
                      />
                    </Box>
                  )}
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        sx={{ bgcolor: "secondary.main", color: "black", mr: 2 }}
                      >
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ color: "text.primary" }}>
                          {testimonial.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "secondary.main", fontWeight: "bold" }}
                        >
                          {[testimonial.company, testimonial.salary]
                            .filter(Boolean)
                            .join(" • ")}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{ color: "text.secondary", lineHeight: 1.6 }}
                    >
                      "{testimonial.quote}"
                    </Typography>
                    {testimonial.loomUrl && (
                      <Button
                        variant="text"
                        color="secondary"
                        href={getLoomShareUrl(testimonial.loomUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mt: 2, px: 0 }}
                      >
                        Watch on Loom
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                color: "black",
                px: 6,
                py: 2,
                fontWeight: "bold",
                fontSize: "1.3rem",
                "&:hover": {
                  transform: "scale(1.05)",
                },
                transition: "transform 0.2s",
              }}
              href={ctaUrl}
              target={ctaTarget}
              rel={ctaRel}
              onClick={handleApplyClick}
            >
              {requireFreeHelpQualification ? ctaLabel : "Join Them - Apply Now"}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: "text.primary", mb: 2 }}
          >
            Questions Before You <span style={{ color: "#88C0D0" }}>Apply?</span>
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              color: "text.secondary",
              mb: 6,
              maxWidth: 760,
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            If you are a software engineer who is currently working but feels
            underpaid, boxed in, or ready for a better role, these are the
            questions I would expect you to have before booking a free advice
            call.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            {faqs.map((faq) => (
              <Paper
                key={faq.question}
                sx={{
                  p: 3,
                  bgcolor: "background.default",
                  border: "1px solid #434C5E",
                  borderRadius: 3,
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "text.primary", fontWeight: "bold", mb: 1.5 }}
                >
                  {faq.question}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", lineHeight: 1.7 }}
                >
                  {faq.answer}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                color: "black",
                px: 6,
                py: 2,
                fontWeight: "bold",
                fontSize: "1.25rem",
                "&:hover": {
                  transform: "scale(1.05)",
                },
                transition: "transform 0.2s",
              }}
              href={ctaUrl}
              target={ctaTarget}
              rel={ctaRel}
              onClick={handleApplyClick}
            >
              {requireFreeHelpQualification ? ctaLabel : "Book Your Free Advice Call"}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ py: 8, bgcolor: "background.paper" }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: 6,
              bgcolor: "rgba(136, 192, 208, 0.1)",
              border: "3px solid #88C0D0",
              borderRadius: 4,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h3"
              gutterBottom
              sx={{ color: "text.primary", mb: 3 }}
            >
              Ready to Land Your Dream Job?
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", mb: 4, lineHeight: 1.6 }}
            >
              Don't let another month pass by without the job you deserve. Every
              day you wait is money lost and opportunities missed.
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                color: "black",
                px: 8,
                py: 3,
                fontWeight: "bold",
                fontSize: "1.4rem",
                mb: 3,
                "&:hover": {
                  transform: "scale(1.05)",
                },
                transition: "transform 0.2s",
              }}
              href={ctaUrl}
              target={ctaTarget}
              rel={ctaRel}
              onClick={handleApplyClick}
            >
              {ctaLabel}
            </Button>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Application review typically takes 24-48 hours
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Back to Home */}
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          color="secondary"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontWeight: "bold",
            borderWidth: 2,
            "&:hover": {
              borderWidth: 2,
              bgcolor: "rgba(136, 192, 208, 0.1)",
            },
          }}
          startIcon={<ArrowBack />}
        >
          Back to Home
        </Button>
      </Box>
    </Box>
  );
};

export default CoachingPage;
